import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Home, 
  Info, 
  Building, 
  FileText, 
  Image, 
  MessageCircle,
  FileCheck
} from 'lucide-react';
import { Typography, Container, Box } from '@mui/material';

const Footer: React.FC = () => {
  const contactInfo = [
    {
      label: 'Reception',
      number: '+91-05946-292491',
      color: 'text-orange-400'
    },
    {
      label: 'Director/Admin',
      number: '+91-9997182574',
      color: 'text-orange-400'
    },
    {
      label: 'Manager',
      number: '+91-9897032340',
      color: 'text-orange-400'
    },
    {
      label: 'Principal',
      number: '+91-8958851161',
      color: 'text-white'
    },
    {
      label: 'Vice Principal',
      number: '+91-9997208173',
      color: 'text-orange-400'
    }
  ];

  const quickLinks = [
    { name: 'Home', icon: <Home size={16} /> },
    { name: 'About Us', icon: <Info size={16} /> },
    { name: 'Facilities', icon: <Building size={16} /> },
    { name: 'Registration', icon: <FileText size={16} /> },
    { name: 'Our Gallery', icon: <Image size={16} /> },
    { name: 'Contact Us', icon: <MessageCircle size={16} /> },
    { name: 'Mandatory Public Disclosure', icon: <FileCheck size={16} /> }
  ];

  return (
    <footer className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white relative overflow-hidden ">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
        <div className="absolute top-20 right-20 w-16 h-16 border border-white transform rotate-45"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 border border-white rounded-full"></div>
        <div className="absolute bottom-10 right-1/3 w-24 h-24 border border-white transform rotate-12"></div>
      </div>
<div className="wave">
    <span></span>
    <span></span>
    <span></span>
  </div>
      <Container maxWidth="lg" className="relative z-10 content">
        <Box py={6}>
          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* About Section */}
            <div className="flex-1 lg:max-w-md">
              <Typography variant="h5" className="font-bold text-white mb-4">
                About
              </Typography>
              <Typography variant="body2" className="text-gray-200 leading-relaxed">
                Ever Green Senior Secondary School stands as a beacon of moral and 
                motivational support for new learners. It holds the distinction of being 
                the first English-medium school established in a remote rural area. This 
                transformative initiative was spearheaded by Late Mr. Luxmi Dutt Pathak, 
                a visionary farmer who utilized his exceptional academic brilliance and 
                unwavering enthusiasm to make education accessible in this region. Thanks 
                to his vision, today Ever Green is Best CBSE Senior Secondary School in 
                Halduchour Haldwani.
              </Typography>
            </div>

            {/* Right Side Container */}
            <div className="flex-1 flex flex-col md:flex-row gap-8">
              {/* Quick Links Section */}
              <div className="flex-1">
                <Typography variant="h5" className="font-bold text-white mb-4">
                  Link
                </Typography>
                <div className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <div key={index} className="flex items-center space-x-2 hover:text-orange-400 transition-colors cursor-pointer">
                      {link.icon}
                      <Typography variant="body2" className="text-gray-200 hover:text-orange-400 transition-colors">
                        {link.name}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact and Get in Touch Container */}
              <div className="flex-1 flex flex-col sm:flex-row md:flex-col gap-8">
                {/* Contact Numbers Section */}
                <div className="flex-1">
                  <Typography variant="h5" className="font-bold text-white mb-4">
                    Contact Number
                  </Typography>
                  <div className="space-y-3">
                    {contactInfo.map((contact, index) => (
                      <div key={index} className="space-y-1">
                        <Typography variant="body2" className={contact.color}>
                          {contact.label}:
                        </Typography>
                        <div className="flex items-center space-x-2">
                          <Phone size={14} className="text-gray-300" />
                          <Typography variant="body2" className="text-gray-200">
                            {contact.number}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Get in Touch Section */}
                <div className="flex-1">
                  <Typography variant="h5" className="font-bold text-white mb-4">
                    Get in Touch
                  </Typography>
                  <Typography variant="body2" className="text-orange-400 mb-4">
                    Always ahead for your solutions.
                  </Typography>
                  
                  {/* Address */}
                  <div className="flex items-start space-x-2 mb-4">
                    <MapPin size={16} className="text-orange-400 mt-1 flex-shrink-0" />
                    <div>
                      <Typography variant="body2" className="text-gray-200">
                        Ever Green Sr. Sec. School Sri Ganga Vihar Colony, Ben Parao, Nainital (Uttarakhand)
                      </Typography>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-orange-400" />
                    <Typography variant="body2" className="text-gray-200 break-all">
                      evergreenpublicschool42@yahoo.in
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <Box mt={6} pt={4} borderTop="1px solid rgba(255,255,255,0.1)">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <Typography variant="body2" className="text-gray-300 text-center md:text-left">
                © 2024 Ever Green Senior Secondary School. All rights reserved.
              </Typography>
              <Typography variant="body2" className="text-gray-300 mt-2 md:mt-0">
                Designed with ❤️ for Education
              </Typography>
            </div>
          </Box>

          {/* Minimal Aesthetic Bottom Bar */}
          <div className="mt-4 pt-3 border-t border-blue-700/30">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
              {/* Left side - Powered by */}
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <span>Powered by</span>
                <span className="text-orange-400 font-medium">AmazeSoft Solutions</span>
              </div>

              {/* Center - Decorative element */}
              <div className="hidden sm:flex items-center gap-1">
                <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                <div className="w-2 h-0.5 bg-gradient-to-r from-orange-400 to-blue-400"></div>
                <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
              </div>

              {/* Right side - Made with love */}
              <div className="text-xs text-gray-400">
                Made with <span className="text-red-400">♥</span> in India
              </div>
            </div>
          </div>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;